//src/pages/concurso.tsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchConcursoById,
  updateConcurso,
  deleteConcurso,
} from "../api/concursosApi";
// <-- Importa fetchAdjudicacionById desde adjudicacionesApi
import { fetchAdjudicacionById } from "../api/adjudicacionesApi";

import { toggleFavorito, fetchFavoritosIds } from "../api/favoritosApi";
import { useAuth } from "../security/AuthLogin";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faHeart,
  faLink,
  faTrash,
  faEdit,
  faSave,
  faTimes,
  faPlus,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";

import LoadingLottie from "../components/LoadingLottie";

// antd:
import { Progress, Modal } from "antd";
import "antd/dist/reset.css";

import "../css/concurso.css";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const { confirm } = Modal;

const Concurso: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuth();

  const [concurso, setConcurso] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Estado para manejar el favorito
  const [isFav, setIsFav] = useState<boolean>(false);
  const [loadingFav, setLoadingFav] = useState<boolean>(true);

  // =========================================================
  //  useEffect principal para cargar concurso -> adjudicación
  // =========================================================
  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      setError(null);
      setLoading(true);

      try {
        // 1) Intentamos fetchConcursoById
        const data = await fetchConcursoById(id);
        setConcurso(data);
      } catch (err: any) {
        // Si err.status=404 => intentamos fetchAdjudicacionById
        if (err.status === 404) {
          try {
            const dataAdju = await fetchAdjudicacionById(id);
            // Redirigir a /adjudicacion/:id
            navigate(`/adjudicacion/${id}`);
            return;
          } catch (err2: any) {
            // Si también 404 => a /
            if (err2.status === 404) {
              navigate("/");
              return;
            } else {
              console.error(err2);
              setError("Error interno al buscar adjudicación");
            }
          }
        } else {
          // Otro error distinto de 404
          console.error(err);
          setError("Error interno al buscar concurso");
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, navigate]);

  // =========================================================
  //  Efecto para verificar si es favorito (no depende del load anterior)
  // =========================================================
  useEffect(() => {
    const checkFavorito = async () => {
      if (!id) return;
      try {
        const favoritos = await fetchFavoritosIds();
        setIsFav(favoritos.includes(id));
      } catch (err) {
        console.error("Error al verificar favorito:", err);
      } finally {
        setLoadingFav(false);
      }
    };
    checkFavorito();
  }, [id]);

  // =========================================================
  //  PDF Viewer
  // =========================================================
  const [pdfViewer, setPdfViewer] = useState({
    isOpen: false,
    link: "",
    name: "",
  });
  const handleViewPDF = (link: string, name: string) => {
    setPdfViewer({ isOpen: true, link, name });
  };
  const closePDFViewer = () => {
    setPdfViewer({ isOpen: false, link: "", name: "" });
  };

  // =========================================================
  //  Funciones de edición
  // =========================================================
  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async () => {
    if (!id || !concurso) return;
    try {
      const updatedConcurso = {
        ...concurso,
        cpv: (concurso.cpv || []).filter((cpv: string) => cpv.trim() !== ""),
        actividad: (concurso.actividad || []).filter(
          (act: string) => act.trim() !== ""
        ),
        documentos: (concurso.documentos || []).filter(
          (doc: any) => doc.link.trim() !== "" && doc.nombre.trim() !== ""
        ),
        criterios_adjudicacion: (concurso.criterios_adjudicacion || []).filter(
          (crit: any) => crit.descripcion && crit.descripcion.trim() !== ""
        ),
        adjudicatario: concurso.adjudicatario || null,
      };
      await updateConcurso(id, updatedConcurso);
      setEditing(false);
      setConcurso(updatedConcurso);
    } catch (err) {
      setError("Error al guardar los cambios");
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteConcurso(id);
      navigate("/concursos");
    } catch (err) {
      setError("Error al eliminar el concurso");
    }
  };

  const confirmDelete = () => {
    confirm({
      title: "¿Estás seguro de que deseas eliminar este concurso?",
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      cancelText: "Cancelar",
      okType: "danger",
      className: "custom-confirm-modal",
      okButtonProps: { className: "custom-ok-button" },
      cancelButtonProps: { className: "custom-cancel-button" },
      onOk() {
        handleDelete();
      },
    });
  };

  // =========================================================
  //  Arrays - CPVs, Actividades, Documentos, Criterios
  // =========================================================
  const handleAddCPV = () => {
    setConcurso({ ...concurso, cpv: ["", ...(concurso.cpv || [])] });
  };

  const handleRemoveCPV = (index: number) => {
    const updatedCPV = [...(concurso.cpv || [])];
    updatedCPV.splice(index, 1);
    setConcurso({ ...concurso, cpv: updatedCPV });
  };

  const handleAddActividad = () => {
    setConcurso({
      ...concurso,
      actividad: ["", ...(concurso.actividad || [])],
    });
  };

  const handleRemoveActividad = (index: number) => {
    const updatedActividad = [...(concurso.actividad || [])];
    updatedActividad.splice(index, 1);
    setConcurso({ ...concurso, actividad: updatedActividad });
  };

  const handleAddDocument = () => {
    setConcurso({
      ...concurso,
      documentos: [{ link: "", nombre: "" }, ...(concurso.documentos || [])],
    });
  };

  const handleRemoveDocument = (index: number) => {
    const updatedDocs = [...(concurso.documentos || [])];
    updatedDocs.splice(index, 1);
    setConcurso({ ...concurso, documentos: updatedDocs });
  };

  const handleAddCriterio = () => {
    setConcurso({
      ...concurso,
      criterios_adjudicacion: [
        { tipo: "", subtipo: "", descripcion: "", ponderacion: 0 },
        ...(concurso.criterios_adjudicacion || []),
      ],
    });
  };

  const handleRemoveCriterio = (index: number) => {
    const updatedCriterios = [...(concurso.criterios_adjudicacion || [])];
    updatedCriterios.splice(index, 1);
    setConcurso({ ...concurso, criterios_adjudicacion: updatedCriterios });
  };

  const handleCriterioChange = (index: number, field: string, value: any) => {
    const updatedCriterios = [...(concurso.criterios_adjudicacion || [])];
    updatedCriterios[index] = { ...updatedCriterios[index], [field]: value };
    setConcurso({ ...concurso, criterios_adjudicacion: updatedCriterios });
  };

  // =========================================================
  //  Fechas
  // =========================================================
  const formatDateForInput = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  };

  const formatDateTimeForInput = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  // =========================================================
  //  Favoritos
  // =========================================================
  const handleToggleFavorito = async () => {
    if (!id) return;
    try {
      const action = isFav ? "remove" : "add";
      await toggleFavorito(id, action);
      setIsFav(!isFav);
    } catch (err) {
      console.error("Error al cambiar favorito:", err);
      alert(
        "Hubo un error al actualizar tu favorito. Por favor, inténtalo de nuevo."
      );
    }
  };

  // =========================================================
  //  Render
  // =========================================================
  if (loading) {
    return <LoadingLottie />;
  }
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  if (!concurso) {
    // Si no hay loading ni error, pero "concurso" es null =>
    // pudo pasar que redireccionamos en la parte de 404
    return null;
  }

  // -----------------------------------
  //  Render normal con tu layout
  // -----------------------------------

  // Variables de fechas
  const now = new Date();
  const fechaPublicacion = concurso.fecha_publicacion
    ? new Date(concurso.fecha_publicacion)
    : null;
  const fechaFinOfertas = concurso.fecha_fin_ofertas
    ? new Date(concurso.fecha_fin_ofertas)
    : null;
  const fechaAdjudicacion = concurso.fecha_adjudicacion
    ? new Date(concurso.fecha_adjudicacion)
    : null;

  // Pasos completados
  const paso1Completo = fechaPublicacion ? now >= fechaPublicacion : false;
  const paso2Completo = fechaFinOfertas ? now >= fechaFinOfertas : false;
  const paso3Completo = fechaAdjudicacion ? now >= fechaAdjudicacion : false;

  // Porcentajes para las barras de progreso
  const barra1Percent = paso1Completo ? (paso2Completo ? 100 : 50) : 0;
  const barra2Percent = paso2Completo ? (paso3Completo ? 100 : 50) : 0;

  // Iconos de pasos
  const iconoPaso1 = paso1Completo
    ? "/images/icons/components/tick.png"
    : "/images/icons/components/circle.png";
  const iconoPaso2 = paso2Completo
    ? "/images/icons/components/tick.png"
    : "/images/icons/components/circle.png";
  const iconoPaso3 = paso3Completo
    ? "/images/icons/components/tick.png"
    : "/images/icons/components/circle.png";

  // Datos para el gráfico de pastel de Criterios de Adjudicación
  let criterios: any[] = [];
  if (concurso.criterios_adjudicacion) {
    if (Array.isArray(concurso.criterios_adjudicacion)) {
      criterios = concurso.criterios_adjudicacion;
    } else {
      if (concurso.criterios_adjudicacion.concursoPrincipal) {
        criterios = criterios.concat(
          concurso.criterios_adjudicacion.concursoPrincipal
        );
      }
      if (concurso.criterios_adjudicacion.lotes) {
        concurso.criterios_adjudicacion.lotes.forEach((lote: any) => {
          criterios = criterios.concat(lote.criterios);
        });
      }
    }
  }

  const criteriosData = criterios.map((criterio: any) => ({
    name: `${criterio.descripcion} (${criterio.subtipo})`,
    value: criterio.ponderacion,
  }));

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#AA46BE",
    "#6F6F6F",
    "#8884D8",
    "#82ca9d",
  ];

  const formatCurrency = (amount: number): string => {
    return (
      new Intl.NumberFormat("es-ES", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount) + " €"
    );
  };

  const formatLink = (link: string): string => {
    if (!link.startsWith("http://") && !link.startsWith("https://")) {
      return `https://${link}`;
    }
    return link;
  };

  const generalInfoFields = [
    { field: "estado", label: "Estado" },
    { field: "provincia", label: "Provincia" },
    { field: "importe_total", label: "Importe total" },
    { field: "duracion", label: "Duración" },
    { field: "importe_estimado", label: "Valor estimado" },
    { field: "prorroga", label: "Prórrogas" },
  ];

  // Campos adicionales editables con placeholders
  const additionalFields = [
    {
      field: "descripcion",
      label: "Descripción",
      type: "textarea",
      placeholder: "Descripción detallada del concurso o licitación",
    },
    {
      field: "localidad",
      label: "Localidad",
      type: "text",
      placeholder: "Ej: Madrid, Barcelona, Valencia",
    },
    {
      field: "codigo_nut",
      label: "Código NUT",
      type: "text",
      placeholder: "Ej: ES220, ES432",
    },
    {
      field: "pais",
      label: "País",
      type: "text",
      placeholder: "Ej: España, Francia, Portugal",
    },
    {
      field: "entidad_compradora",
      label: "Entidad Compradora",
      type: "text",
      placeholder: "Nombre de la entidad o empresa compradora",
    },
    {
      field: "entidad_contacto",
      label: "Entidad Contacto",
      type: "text",
      placeholder: "Entidad responsable del contacto",
    },
    {
      field: "direccion",
      label: "Dirección",
      type: "text",
      placeholder: "Dirección completa de la entidad",
    },
    {
      field: "telefono",
      label: "Teléfono",
      type: "text",
      placeholder: "Ej: 913963667, +34 913963667",
    },
    {
      field: "email",
      label: "Email",
      type: "email",
      placeholder: "Ej: contratacion@empresa.es",
    },
    {
      field: "url_perfil_comprador",
      label: "URL Perfil Comprador",
      type: "text",
      placeholder: "URL del perfil del comprador",
    },
    {
      field: "tipo_contrato",
      label: "Tipo Contrato",
      type: "text",
      placeholder: "Ej: Obras, Servicios, Suministros",
    },
    {
      field: "subtipo_contrato",
      label: "Subtipo Contrato",
      type: "text",
      placeholder: "Ej: Construcción, Alquiler de equipo, Consultoría",
    },
    {
      field: "enlace",
      label: "Enlace",
      type: "text",
      placeholder: "URL del concurso en la plataforma oficial",
    },
  ];

  return (
    <div className="concurso-container">
      <div className="concurso-header">
        <div className="concurso-titulo">
          {editing ? (
            <>
              <h1>
                Expediente:
                <input
                  type="text"
                  value={concurso.codigo_expediente || ""}
                  placeholder="Ej: TSA0078927_019_NA"
                  onChange={(e) =>
                    setConcurso({
                      ...concurso,
                      codigo_expediente: e.target.value,
                    })
                  }
                  style={{ marginLeft: "10px", fontSize: "inherit" }}
                />
              </h1>
              <textarea
                value={concurso.titulo || ""}
                placeholder="Título completo del concurso o licitación"
                onChange={(e) =>
                  setConcurso({ ...concurso, titulo: e.target.value })
                }
                style={{ width: "100%", minHeight: "60px" }}
              />
            </>
          ) : (
            <>
              <h1>Expediente: {concurso.codigo_expediente}</h1>
              <p>{concurso.titulo}</p>
            </>
          )}
        </div>
        <div className="concurso-actions">
          {/* Botón de Favorito (solo usuarios normales) */}
          {userRole !== "admin" && (
            <FontAwesomeIcon
              icon={faHeart}
              title={isFav ? "Quitar de favoritos" : "Añadir a favoritos"}
              style={{
                cursor: "pointer",
                color: isFav ? "#b90029" : "#555",
                fontSize: "1.5rem",
              }}
              onClick={handleToggleFavorito}
            />
          )}

          {/* Botón Ver en Fuente */}
          {concurso.enlace && (
            <a
              href={formatLink(concurso.enlace)}
              target="_blank"
              rel="noreferrer"
            >
              <FontAwesomeIcon
                icon={faLink}
                title="Ver en fuente"
                style={{ fontSize: "1.5rem" }}
              />
            </a>
          )}

          {/* Editar y Eliminar (admin) */}
          {!editing && userRole === "admin" && (
            <>
              <FontAwesomeIcon
                icon={faEdit}
                title="Editar Concurso"
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                onClick={handleEdit}
              />
              <FontAwesomeIcon
                icon={faTrash}
                title="Eliminar Concurso"
                style={{ cursor: "pointer", fontSize: "1.5rem" }}
                onClick={confirmDelete}
              />
            </>
          )}
        </div>
      </div>

      <section className="section general-info">
        <h2>Información General</h2>
        <div className="info-grid">
          {generalInfoFields.map(({ field, label }) => (
            <div className="field" key={field}>
              <label>{label}:</label>
              {editing ? (
                field === "importe_total" || field === "importe_estimado" ? (
                  <input
                    type="number"
                    value={concurso[field] || ""}
                    placeholder="Ej: 56000.00"
                    step="0.01"
                    onChange={(e) =>
                      setConcurso({
                        ...concurso,
                        [field]: parseFloat(e.target.value),
                      })
                    }
                  />
                ) : field === "prorroga" ? (
                  <textarea
                    value={concurso.prorroga || ""}
                    placeholder="Información sobre prórrogas del contrato"
                    onChange={(e) =>
                      setConcurso({ ...concurso, prorroga: e.target.value })
                    }
                  />
                ) : field === "estado" ? (
                  <select
                    value={concurso[field] || ""}
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  >
                    <option value="">Seleccione estado</option>
                    <option value="Anuncio previo">Anuncio previo</option>
                    <option value="En plazo">En plazo</option>
                    <option value="Pdte adjudicación">Pdte adjudicación</option>
                    <option value="Adjudicada">Adjudicada</option>
                    <option value="Resuelta">Resuelta</option>
                    <option value="Anulada">Anulada</option>
                  </select>
                ) : field === "duracion" ? (
                  <input
                    type="text"
                    value={concurso[field] || ""}
                    placeholder="Ej: 7 meses, 2 años, 180 días"
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  />
                ) : field === "provincia" ? (
                  <input
                    type="text"
                    value={concurso[field] || ""}
                    placeholder="Ej: Madrid, Barcelona, Valencia"
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  />
                ) : (
                  <input
                    type="text"
                    value={concurso[field] || ""}
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  />
                )
              ) : (
                <span>
                  {field === "importe_total" || field === "importe_estimado"
                    ? concurso[field]
                      ? formatCurrency(concurso[field])
                      : "N/A"
                    : field === "duracion"
                    ? concurso.duracion || "N/A"
                    : field === "prorroga"
                    ? concurso.prorroga || "N/A"
                    : field === "provincia"
                    ? concurso.provincia || concurso.localidad || "N/A"
                    : concurso[field] || "N/A"}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Sección de campos adicionales cuando esté editando */}
      {editing && (
        <section className="section additional-info">
          <h2>Información Adicional</h2>
          <div className="info-grid">
            {additionalFields.map(({ field, label, type, placeholder }) => (
              <div className="field" key={field}>
                <label>{label}:</label>
                {type === "textarea" ? (
                  <textarea
                    value={concurso[field] || ""}
                    placeholder={placeholder}
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  />
                ) : (
                  <input
                    type={type}
                    value={concurso[field] || ""}
                    placeholder={placeholder}
                    onChange={(e) =>
                      setConcurso({ ...concurso, [field]: e.target.value })
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <hr className="line" />

      {/* Progreso del concurso */}
      <section className="project-overview progress-bar-section">
        <div className="top-dates">
          <div>
            <p className="date-title" style={{ marginBottom: "0.5rem" }}>
              Fecha de publicación
            </p>
            {editing ? (
              <input
                type="date"
                value={formatDateForInput(concurso.fecha_publicacion)}
                onChange={(e) =>
                  setConcurso({
                    ...concurso,
                    fecha_publicacion: e.target.value,
                  })
                }
              />
            ) : (
              <p className="date-title" style={{ marginBottom: "0.5rem" }}>
                {fechaPublicacion
                  ? fechaPublicacion.toLocaleDateString("es-ES")
                  : "No disponible"}
              </p>
            )}
          </div>
          <div>
            <p
              className="date-title"
              style={{ marginBottom: "0.5rem", textAlign: "right" }}
            >
              Fecha de adjudicación
            </p>
            {editing ? (
              <input
                type="date"
                value={formatDateForInput(concurso.fecha_adjudicacion)}
                onChange={(e) =>
                  setConcurso({
                    ...concurso,
                    fecha_adjudicacion: e.target.value,
                  })
                }
                style={{ textAlign: "right" }}
              />
            ) : (
              <p
                className="date-title"
                style={{ marginBottom: "0.5rem", textAlign: "right" }}
              >
                {fechaAdjudicacion
                  ? fechaAdjudicacion.toLocaleDateString("es-ES")
                  : "No disponible"}
              </p>
            )}
          </div>
        </div>

        <div className="progress-bar-with-points">
          {/* Punto 1 */}
          <div className="progress-point">
            <img src={iconoPaso1} alt="Paso 1" />
          </div>

          {/* Barra 1 */}
          <div className="progress-bar-segment">
            <Progress
              percent={barra1Percent}
              showInfo={false}
              strokeColor="#1890ff"
            />
          </div>

          {/* Punto 2 */}
          <div className="progress-point">
            <img src={iconoPaso2} alt="Paso 2" />
          </div>

          {/* Barra 2 */}
          <div className="progress-bar-segment">
            <Progress
              percent={barra2Percent}
              showInfo={false}
              strokeColor="#1890ff"
            />
          </div>

          {/* Punto 3 */}
          <div className="progress-point">
            <img src={iconoPaso3} alt="Paso 3" />
          </div>
        </div>

        <div className="bottom-dates">
          <div>
            {editing ? (
              <>
                <input
                  type="datetime-local"
                  value={formatDateTimeForInput(concurso.fecha_fin_ofertas)}
                  onChange={(e) =>
                    setConcurso({
                      ...concurso,
                      fecha_fin_ofertas: e.target.value,
                    })
                  }
                />
                <p className="date-title" style={{ marginTop: "0.5rem" }}>
                  Fecha límite de presentación
                </p>
              </>
            ) : (
              <>
                <p
                  className="date-title"
                  style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}
                >
                  {fechaFinOfertas
                    ? fechaFinOfertas.toLocaleString("es-ES", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : "No disponible"}
                </p>
                <p
                  className="date-title"
                  style={{ marginTop: "0.5rem", marginBottom: "0.5rem" }}
                >
                  Fecha límite de presentación
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <hr className="line" />

      <section className="section adjudicacion-section">
        <h2>Información de Adjudicación</h2>

        {/* Criterios de Adjudicación */}
        <div className="field adjudicacion-criterios-full">
          <label>Criterios de Adjudicación:</label>
          {editing && (
            <button className="btn btn-add" onClick={handleAddCriterio}>
              <FontAwesomeIcon icon={faPlus} /> Añadir Criterio
            </button>
          )}
          {criteriosData.length > 0 || editing ? (
            <div className="criterios-container">
              {editing ? (
                <div className="criterios-edit">
                  {(concurso.criterios_adjudicacion || []).map(
                    (criterio: any, index: number) => (
                      <div key={index} className="criterio-edit-item">
                        <div className="criterio-fields">
                          <input
                            type="text"
                            placeholder="Tipo (Ej: Juicio de Valor)"
                            value={criterio.tipo || ""}
                            onChange={(e) =>
                              handleCriterioChange(
                                index,
                                "tipo",
                                e.target.value
                              )
                            }
                          />
                          <input
                            type="text"
                            placeholder="Subtipo (Ej: Precio, Otros)"
                            value={criterio.subtipo || ""}
                            onChange={(e) =>
                              handleCriterioChange(
                                index,
                                "subtipo",
                                e.target.value
                              )
                            }
                          />
                          <input
                            type="number"
                            placeholder="% (Ej: 60)"
                            min="0"
                            max="100"
                            step="0.01"
                            value={criterio.ponderacion || ""}
                            onChange={(e) =>
                              handleCriterioChange(
                                index,
                                "ponderacion",
                                parseFloat(e.target.value) || 0
                              )
                            }
                          />
                          <input
                            type="text"
                            placeholder="Descripción (Ej: Oferta económica)"
                            value={criterio.descripcion || ""}
                            onChange={(e) =>
                              handleCriterioChange(
                                index,
                                "descripcion",
                                e.target.value
                              )
                            }
                          />
                          <button
                            className="btn btn-remove"
                            onClick={() => handleRemoveCriterio(index)}
                          >
                            <FontAwesomeIcon icon={faMinus} />
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <>
                  <div className="criterios-pie-chart">
                    <PieChart width={400} height={400}>
                      <Pie
                        data={criteriosData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ value }) => `${value}%`}
                      >
                        {criteriosData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            // Obtener el índice del segmento actual para determinar el color
                            const index = criteriosData.findIndex(
                              (item) => item.name === payload[0].name
                            );
                            const color = COLORS[index % COLORS.length];

                            return (
                              <div className="custom-tooltip">
                                <div
                                  className="tooltip-color-bar"
                                  style={{ backgroundColor: color }}
                                ></div>
                                <div className="tooltip-content">
                                  <div className="tooltip-header">
                                    <span className="tooltip-title">
                                      {payload[0].name}
                                    </span>
                                  </div>
                                  <div className="tooltip-value">
                                    <span className="value-number">
                                      {payload[0].value}%
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </div>
                  <div className="criterios-legend">
                    <ul>
                      {criteriosData.map((entry, index) => (
                        <li key={`legend-${index}`}>
                          <span
                            className="legend-color"
                            style={{
                              backgroundColor: COLORS[index % COLORS.length],
                            }}
                          ></span>
                          <span className="legend-text">
                            {entry.name}: {entry.value}%
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          ) : (
            <span className="no-data">
              No hay criterios de adjudicación disponibles
            </span>
          )}
        </div>

        {/* Demás info de adjudicación */}
        <div className="info-grid">
          {/* Adjudicatarios */}
          <div className="field adjudicacion-adjudicatarios">
            <label>Adjudicatarios:</label>
            {editing ? (
              <div className="adjudicatarios-edit">
                <input
                  type="text"
                  placeholder="NIF (Ej: A26113167)"
                  value={
                    concurso.adjudicatario?.adjudicatario?.nif ||
                    concurso.adjudicatario?.nif ||
                    ""
                  }
                  onChange={(e) => {
                    const newAdjudicatario = concurso.adjudicatario
                      ?.adjudicatario
                      ? {
                          ...concurso.adjudicatario,
                          adjudicatario: {
                            ...concurso.adjudicatario.adjudicatario,
                            nif: e.target.value,
                          },
                        }
                      : {
                          adjudicatario: {
                            nif: e.target.value,
                            name: concurso.adjudicatario?.name || "",
                            cityName: concurso.adjudicatario?.cityName || "",
                          },
                        };
                    setConcurso({
                      ...concurso,
                      adjudicatario: newAdjudicatario,
                    });
                  }}
                />
                <input
                  type="text"
                  placeholder="Nombre de la empresa (Ej: TRANSPORTES MAPILO, S.A.)"
                  value={
                    concurso.adjudicatario?.adjudicatario?.name ||
                    concurso.adjudicatario?.name ||
                    ""
                  }
                  onChange={(e) => {
                    const newAdjudicatario = concurso.adjudicatario
                      ?.adjudicatario
                      ? {
                          ...concurso.adjudicatario,
                          adjudicatario: {
                            ...concurso.adjudicatario.adjudicatario,
                            name: e.target.value,
                          },
                        }
                      : {
                          adjudicatario: {
                            nif: concurso.adjudicatario?.nif || "",
                            name: e.target.value,
                            cityName: concurso.adjudicatario?.cityName || "",
                          },
                        };
                    setConcurso({
                      ...concurso,
                      adjudicatario: newAdjudicatario,
                    });
                  }}
                />
                <input
                  type="text"
                  placeholder="Ciudad y provincia (Ej: Alfaro (La Rioja), España)"
                  value={
                    concurso.adjudicatario?.adjudicatario?.cityName ||
                    concurso.adjudicatario?.cityName ||
                    ""
                  }
                  onChange={(e) => {
                    const newAdjudicatario = concurso.adjudicatario
                      ?.adjudicatario
                      ? {
                          ...concurso.adjudicatario,
                          adjudicatario: {
                            ...concurso.adjudicatario.adjudicatario,
                            cityName: e.target.value,
                          },
                        }
                      : {
                          adjudicatario: {
                            nif: concurso.adjudicatario?.nif || "",
                            name: concurso.adjudicatario?.name || "",
                            cityName: e.target.value,
                          },
                        };
                    setConcurso({
                      ...concurso,
                      adjudicatario: newAdjudicatario,
                    });
                  }}
                />
              </div>
            ) : (
              <>
                {concurso.adjudicatario ? (
                  Array.isArray(concurso.adjudicatario) ? (
                    concurso.adjudicatario.length > 0 ? (
                      <ul className="adjudicatarios-list">
                        {concurso.adjudicatario.map(
                          (item: any, index: number) => (
                            <li key={index} className="adjudicatario-item">
                              <strong>{item.lote || "General"}:</strong>{" "}
                              {item.adjudicatario?.name} (
                              {item.adjudicatario?.nif || "N/A"})
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      <span className="no-data">
                        No adjudicatarios disponibles
                      </span>
                    )
                  ) : typeof concurso.adjudicatario === "object" ? (
                    concurso.adjudicatario.adjudicatario ? (
                      <span>
                        {concurso.adjudicatario.adjudicatario.name} (
                        {concurso.adjudicatario.adjudicatario.nif || "N/A"})
                      </span>
                    ) : (
                      <span>
                        {concurso.adjudicatario.name} (
                        {concurso.adjudicatario.nif || "N/A"})
                      </span>
                    )
                  ) : (
                    <span className="no-data">
                      No adjudicatarios disponibles
                    </span>
                  )
                ) : (
                  <span className="no-data">No adjudicatarios disponibles</span>
                )}
              </>
            )}
          </div>

          {/* Resultado */}
          <div className="field">
            <label>Resultado del Procedimiento:</label>
            {editing ? (
              <input
                type="text"
                value={concurso.resultado_adjudicacion || ""}
                placeholder="Ej: Adjudicado, Desierto, Anulado"
                onChange={(e) =>
                  setConcurso({
                    ...concurso,
                    resultado_adjudicacion: e.target.value,
                  })
                }
              />
            ) : (
              <span>
                {concurso.resultado_adjudicacion ||
                  "No hay resultados para este concurso / lote"}
              </span>
            )}
          </div>

          {/* Importe de Adjudicación */}
          <div className="field">
            <label>Importe de Adjudicación:</label>
            {editing ? (
              <input
                type="number"
                value={concurso.importe_adjudicacion || ""}
                placeholder="Ej: 56000.00"
                step="0.01"
                onChange={(e) =>
                  setConcurso({
                    ...concurso,
                    importe_adjudicacion: parseFloat(e.target.value),
                  })
                }
              />
            ) : (
              <span>
                {concurso.importe_adjudicacion
                  ? formatCurrency(concurso.importe_adjudicacion)
                  : "No hay importe de adjudicación establecido"}
              </span>
            )}
          </div>

          {/* Número de Licitadores */}
          <div className="field">
            <label>Número de Licitadores:</label>
            {editing ? (
              <input
                type="number"
                value={concurso.numero_licitadores || ""}
                placeholder="Ej: 2, 5, 12"
                min="0"
                onChange={(e) =>
                  setConcurso({
                    ...concurso,
                    numero_licitadores: parseInt(e.target.value) || 0,
                  })
                }
              />
            ) : (
              <span>{concurso.numero_licitadores || "No hay licitadores"}</span>
            )}
          </div>
        </div>
      </section>

      <hr className="line" />

      <section className="section cpvs-section">
        <h2>CPVs</h2>
        {editing && (
          <button className="btn btn-add" onClick={handleAddCPV}>
            <FontAwesomeIcon icon={faPlus} /> Añadir CPV
          </button>
        )}
        <ul>
          {concurso.cpv?.map((cpv: string, index: number) => (
            <li key={index}>
              {editing ? (
                <div className="cpv-edit">
                  <input
                    type="text"
                    value={cpv}
                    placeholder="Ej: 45000000, 77500000"
                    onChange={(e) =>
                      setConcurso({
                        ...concurso,
                        cpv: concurso.cpv.map((c: string, i: number) =>
                          i === index ? e.target.value : c
                        ),
                      })
                    }
                  />
                  {userRole === "admin" && (
                    <button
                      className="btn btn-remove"
                      onClick={() => handleRemoveCPV(index)}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  )}
                </div>
              ) : (
                <span>{cpv}</span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <hr className="line" />

      {/* Sección de Actividades */}
      {/* <section className="section actividades-section">
        <h2>Actividades</h2>
        {editing && (
          <button className="btn btn-add" onClick={handleAddActividad}>
            <FontAwesomeIcon icon={faPlus} /> Añadir Actividad
          </button>
        )}
        {concurso.actividad && concurso.actividad.length > 0 ? (
          <ul>
            {concurso.actividad.map((actividad: string, index: number) => (
              <li key={index}>
                {editing ? (
                  <div className="actividad-edit">
                    <input
                      type="text"
                      value={actividad}
                      placeholder="Ej: Protección del medio ambiente, Servicios públicos generales"
                      onChange={(e) =>
                        setConcurso({
                          ...concurso,
                          actividad: concurso.actividad.map(
                            (a: string, i: number) =>
                              i === index ? e.target.value : a
                          ),
                        })
                      }
                    />
                    <button
                      className="btn btn-remove"
                      onClick={() => handleRemoveActividad(index)}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                  </div>
                ) : (
                  <span>{actividad}</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay actividades disponibles.</p>
        )}
      </section>

      <hr className="line" /> */}

      <section className="section documents-section">
        <h2>Documentos</h2>
        {editing && (
          <button className="btn btn-add" onClick={handleAddDocument}>
            <FontAwesomeIcon icon={faPlus} /> Añadir Documento
          </button>
        )}
        {concurso.documentos && concurso.documentos.length > 0 ? (
          <ul>
            {concurso.documentos.map((doc: any, index: number) => (
              <li key={index} className="document-item">
                {editing ? (
                  <div className="document-edit">
                    <input
                      type="text"
                      placeholder="Nombre del documento (Ej: PCAP TSA0078927 AM.pdf)"
                      value={doc.nombre}
                      onChange={(e) =>
                        setConcurso({
                          ...concurso,
                          documentos: concurso.documentos.map(
                            (d: any, i: number) =>
                              i === index ? { ...d, nombre: e.target.value } : d
                          ),
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="URL del documento"
                      value={doc.link}
                      onChange={(e) =>
                        setConcurso({
                          ...concurso,
                          documentos: concurso.documentos.map(
                            (d: any, i: number) =>
                              i === index
                                ? { ...d, link: formatLink(e.target.value) }
                                : d
                          ),
                        })
                      }
                    />
                    {userRole === "admin" && (
                      <button
                        className="btn btn-remove"
                        onClick={() => handleRemoveDocument(index)}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="document-info">
                    <span>{doc.nombre}</span>
                    <div className="document-buttons">
                      {/* Ver Aquí */}
                      <button
                        className="btn btn-view-here"
                        onClick={() => handleViewPDF(doc.link, doc.nombre)}
                        title="Ver aquí"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      {/* Ver en fuente */}
                      <a
                        className="btn btn-view-source"
                        href={formatLink(doc.link)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ver en fuente"
                      >
                        <FontAwesomeIcon icon={faLink} />
                      </a>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No hay documentos disponibles.</p>
        )}
      </section>

      {editing && (
        <div className="actions">
          <button className="btn btn-guardar" onClick={handleSave}>
            <FontAwesomeIcon icon={faSave} /> Guardar
          </button>
          <button
            className="btn btn-cancelar"
            onClick={() => {
              setEditing(false);
              setError(null);
            }}
          >
            <FontAwesomeIcon icon={faTimes} /> Cancelar
          </button>
        </div>
      )}

      {/* Visor PDF */}
      {pdfViewer.isOpen && (
        <div className="pdf-viewer-overlay" onClick={closePDFViewer}>
          <div className="pdf-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <span className="pdf-title">{pdfViewer.name}</span>
              <button className="btn btn-close" onClick={closePDFViewer}>
                X
              </button>
            </div>
            <iframe
              src={pdfViewer.link}
              title="Visor PDF"
              width="100%"
              height="600px"
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default Concurso;
